import type { LinhaMapeada } from './validacao'

export interface Condicao {
  numero: number // 1-based
  linhas: LinhaMapeada[]
}

export interface Agrupamento {
  condicoes: Condicao[]
  unicos: Array<{ codigo: string; descricao: string }>
  repetidos: string[]
}

// A fornecedora vende o MESMO produto em mais de uma condicao (preco x
// comissao) e manda as duas no mesmo arquivo, com o codigo repetido. Aqui a
// enesima ocorrencia de cada codigo vira a condicao N.
//
// Antes disto, validarProdutos descartava a 2a ocorrencia em silencio e ficava
// com a 1a -- se a condicao desejada fosse a de baixo, entrava preco errado sem
// aviso nenhum. Ver a spec de 2026-07-28.
//
// Ordem de aparicao, e nao "coluna que separa" nem "preco maior/menor": funciona
// tanto em blocos (linhas 1-22 e 23-44, o arquivo da Forte Pan) quanto
// intercalado, e nao depende de coluna que pode vir vazia.
export function agruparCondicoes(linhas: LinhaMapeada[]): Agrupamento {
  // Map preserva a ordem de insercao: e o que faz `repetidos` sair na ordem de
  // primeira aparicao, que e a ordem em que a tela mostra os exemplos.
  const porCodigo = new Map<string, LinhaMapeada[]>()
  for (const linha of linhas) {
    const codigo = linha.campos.codigo
    if (!codigo) continue // sem codigo nao ha o que agrupar; validarProdutos reprova
    const ocorrencias = porCodigo.get(codigo)
    if (ocorrencias) ocorrencias.push(linha)
    else porCodigo.set(codigo, [linha])
  }

  const ocorrencias = [...porCodigo.values()]
  const total = ocorrencias.reduce((maior, o) => Math.max(maior, o.length), 0)

  const condicoes: Condicao[] = []
  for (let n = 0; n < total; n++) {
    const daCondicao: LinhaMapeada[] = []
    for (const doCodigo of ocorrencias) {
      // Codigo de ocorrencia unica entra em TODAS as condicoes, com o unico
      // preco que tem: decisao da usuaria (2026-07-28), para nao gerar tabela
      // furada. A tela avisa quais sao.
      const linha = doCodigo.length === 1 ? doCodigo[0] : doCodigo[n]
      if (linha) daCondicao.push(linha)
    }
    condicoes.push({
      numero: n + 1,
      linhas: daCondicao.sort((a, b) => a.indice - b.indice),
    })
  }

  return {
    condicoes,
    unicos: ocorrencias
      .filter((o) => o.length === 1)
      .map((o) => ({ codigo: o[0].campos.codigo, descricao: o[0].campos.descricao })),
    repetidos: ocorrencias.filter((o) => o.length > 1).map((o) => o[0].campos.codigo),
  }
}

// Quais colunas CRUAS da planilha mudam entre as condicoes do mesmo codigo.
//
// Cruas de proposito: a tela precisa mostrar o que a usuaria reconhece, e o
// diferenciador dela e o "% DESC" -- uma coluna que nem existe como campo
// (CAMPOS_PRODUTOS, mapeamento.ts) e que so poderia ser ignorada no mapeamento.
// Comparando as colunas originais, o desconto aparece sem precisar de campo
// novo, e a mesma logica serve para fornecedoras cujo diferenciador e outro.
export function colunasQueDiferem(
  colunas: string[],
  linhas: string[][],
  condicoes: Condicao[],
): number[] {
  if (condicoes.length < 2) return []

  // codigo -> indices de linha (1-based) em que ele aparece. Codigo de
  // ocorrencia unica repete o MESMO indice em todas as condicoes, entao o Set
  // deixa ele com tamanho 1 e ele cai fora da comparacao sozinho -- que e o
  // certo: nao ha com o que comparar.
  const indicesPorCodigo = new Map<string, Set<number>>()
  for (const condicao of condicoes) {
    for (const linha of condicao.linhas) {
      const atual = indicesPorCodigo.get(linha.campos.codigo)
      if (atual) atual.add(linha.indice)
      else indicesPorCodigo.set(linha.campos.codigo, new Set([linha.indice]))
    }
  }
  const comparaveis = [...indicesPorCodigo.values()].filter((s) => s.size > 1)

  const diferentes: number[] = []
  for (let coluna = 0; coluna < colunas.length; coluna++) {
    const difere = comparaveis.some((indices) => {
      const valores = new Set(
        [...indices].map((indice) => (linhas[indice - 1]?.[coluna] ?? '').trim()),
      )
      return valores.size > 1
    })
    if (difere) diferentes.push(coluna)
  }
  return diferentes
}
